import React, { useState, useEffect, useCallback } from "react";
import { DefaultAndAuthProps } from "model/props";

import css from "./suborganisationsscreen.module.scss";
import PageHeader from "../../components/pageheader/PageHeader";
import { getLanguageString as _ } from "misc/lang";
import Search from "components/search/Search";
import FormButton from "components/form/FormButton";
import { Paths } from "appConfig";
import DataTable, { Column } from "components/datatable/DataTable";
import {
  apiGetHomesList,
  apiHomeDelete,
  apiGetAccountList,
  apiGetHrAccountList,
} from "net/requests";
import { Home } from "model/home";
import { Organisation } from "model/organisation";
import ConfirmDialog from "components/confirmdialog/ConfirmDialog";
import { Alert, Snackbar } from "@mui/material";

const SubOrganisationsScreen: React.FC<DefaultAndAuthProps> = (props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [suborganizations, setSuborganizations] = useState<Home[]>([]);
  const [organizations, setOrganizations] = useState<Organisation[]>([]);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    item: any;
  }>({ open: false, item: null });

  const { admin, navigate, account, setAccount, globalAppMode } = props;

  const handleCreateNew = () => {
    if (!account?.uuid) {
      setNotification({
        open: true,
        message: "Please select an organisation first",
        severity: "error",
      });
      return;
    }
    navigate(Paths.ADD_SUB_ORGANISATIONS);
  };

  const mode = admin?.is_super_admin
    ? globalAppMode
    : account?.type?.toUpperCase();

  const columns: Column[] = [
    {
      key: "name",
      label: _(
        "organisation",
        mode === "HEALTH"
          ? "health_sub_organisation_name"
          : "organisation_name",
      ),
    },
    { key: "location", label: _("organisation", "location") },
  ];

  const actionLoadHomes = useCallback(() => {
    console.log("LOAD HOMES FUNCTION!");
    // If super admin and no specific account selected, load ALL homes
    if (admin?.is_super_admin && !account?.uuid) {
      console.log("super admin - load all homes");
      apiGetHomesList(admin)
        .then((response) => {
          const homes: Home[] = response.data;
          setSuborganizations(homes);
        })
        .catch((error) => {
          const errorCode = error.response?.data?.error;
          console.log(errorCode);
        });
      return;
    }

    if (!account?.uuid) {
      console.log("No account selected, cannot load homes");
      return;
    }

    if (mode !== "HEALTH") {
      console.log(
        "HR MODE - Load sub-organisations for account:",
        account.uuid,
      );
      // HR MODE - Use /hr/account/list with parent_uuid
      apiGetHrAccountList(admin, account.uuid)
        .then((response) => {
          const homes: Home[] = response.data;
          setSuborganizations(homes);
        })
        .catch((error) => {
          const errorCode = error.response?.data?.error;
          console.log(errorCode);
        });
    } else {
      // HEALTH MODE - Use original apiGetHomesList
      apiGetHomesList(admin, account.uuid)
        .then((response) => {
          const homes: Home[] = response.data;
          setSuborganizations(homes);
        })
        .catch((error) => {
          const errorCode = error.response?.data?.error;
          console.log(errorCode);
        });
    }
  }, [admin, account?.uuid, mode]);

  const handleDelete = (item: any) => {
    setDeleteDialog({ open: true, item });
  };

  const confirmDelete = async () => {
    if (!admin || !deleteDialog.item) return;

    try {
      await apiHomeDelete(admin, deleteDialog.item.uuid);
      // Remove from local state immediately
      setSuborganizations((prev) =>
        prev.filter((r) => r.uuid !== deleteDialog.item.uuid),
      );
      setNotification({
        open: true,
        message: _("organisation", "delete_sub_org_success"),
        severity: "success",
      });
      setDeleteDialog({ open: false, item: null });
    } catch (error) {
      console.error("Error deleting suborganization:", error);
      setNotification({
        open: true,
        message: _("organisation", "delete_sub_org_error"),
        severity: "error",
      });
      setDeleteDialog({ open: false, item: null });
    }
  };

  const handleRowClick = (item: any) => {
    navigate(Paths.EDIT_SUB_ORGANISATIONS.replace(":id", item.uuid));
  };

  const tableData = suborganizations
    .filter((home) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        home.name?.toLowerCase().includes(query) ||
        home.address_line1?.toLowerCase().includes(query) ||
        home.address_city?.toLowerCase().includes(query)
      );
    })
    .map((home) => ({
      uuid: home.uuid,
      name: home.name,
      location: `${home.address_line1}, ${home.address_city}`,
    }));

  useEffect(() => {
    if (account?.uuid) {
      actionLoadHomes();
    }
  }, [account?.uuid, actionLoadHomes]);

  useEffect(() => {
    if (!account?.uuid) {
      setSuborganizations([]);
    }
  }, [account?.uuid]);

  useEffect(() => {
    if (admin?.is_super_admin) {
      apiGetAccountList(admin, globalAppMode)
        .then((response) => {
          const orgs: Organisation[] = response.data;
          const topLevelOrgs = orgs.filter((o: Organisation) => !o.parent_uuid);
          setOrganizations(topLevelOrgs);
        })
        .catch((error) => {
          console.error("Error loading organizations:", error);
        });
    }
  }, [admin, globalAppMode]);

  console.log("admin is", admin);

  return (
    <div>
      <div className={css.container}>
        <PageHeader
          title={`${_(
            "organisation",
            mode === "HEALTH"
              ? "health_sub_organisations_title"
              : "sub_organisations_title",
          )} ${account?.name || "..."}`}
          subtitle={_(
            "organisation",
            mode === "HEALTH"
              ? "health_sub_organisations_subtitle"
              : "sub_organisations_subtitle",
          )}
        />

        {/* Organization selector */}
        <div className={css.organizationSelectorOuter}>
          <div className={css.organizationSelector}>
            {admin?.is_super_admin &&
              organizations.map((org) => (
                <button
                  key={org.uuid}
                  className={`${css.orgButton} ${
                    account?.uuid === org.uuid ? css.selected : ""
                  }`}
                  onClick={() => setAccount(org as any)}
                >
                  {org.name}
                </button>
              ))}
          </div>
        </div>

        <div className={css.contentBox}>
          {suborganizations.length === 0 && (
            <div className={css.emptyState}>
              <div className={css.title}>
                {_(
                  "organisation",
                  mode === "HEALTH"
                    ? "health_no_sub_organisations_title"
                    : "no_sub_organisations_title",
                )}
              </div>
              <div className={css.subtitle}>
                {_(
                  "organisation",
                  mode === "HEALTH"
                    ? "health_no_sub_organisations_subtitle"
                    : "no_sub_organisations_subtitle",
                )}
              </div>
            </div>
          )}
          <div className={css.searchRow}>
            <Search
              placeholder={_("general", "search")}
              value={searchQuery}
              onChange={setSearchQuery}
            />
            <FormButton
              label={_("organisation", "create_new")}
              onSubmit={handleCreateNew}
              inline={true}
              variant="contained"
              color="darkerGreen"
            />
          </div>

          {suborganizations.length > 0 && (
            <div className={css.tableBox}>
              <DataTable
                columns={columns}
                data={tableData}
                onDelete={handleDelete}
                onRowClick={handleRowClick}
              />
            </div>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={deleteDialog.open}
        title={_("organisation", "confirm_delete_sub_org_title")}
        message={_("organisation", "confirm_delete_sub_org_message")}
        itemName={deleteDialog.item?.name}
        confirmLabel={_("organisation", "confirm_delete_yes")}
        cancelLabel={_("organisation", "cancel")}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialog({ open: false, item: null })}
      />
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SubOrganisationsScreen;
